import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { MeValidation } from "./me-validation";
import { TranslateUtilityService } from "src/app/system/utility/translation-utility.service";

export class MeFormatValidation extends MeValidation {

    errorKey: string;
    readablePattern: string;
    override hasEndingPeriod: boolean;

    constructor(translate : TranslateService | TranslateUtilityService, public pattern: string, 
      errorKey : string = 'MESSAGE_ERROR_VALID_INPUT_ONLY', readablePattern: string = '', hasEndingPeriod: boolean = true) {
        super(translate, 'pattern');

        this.errorKey = errorKey;
        this.readablePattern = readablePattern;
        this.hasEndingPeriod = hasEndingPeriod;
        this.validators = this.patternValidator(new RegExp(`^${pattern}$`));
    }

    getErrorMessage(): string {
      if(this.errorKey)  return this.translate.instant(this.errorKey) + ' ' + this.readablePattern;
      else return this.readablePattern;
    }

    patternValidator(pattern: RegExp): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) return null;
  
        // Normalize input: Replace iOS-specific characters with standard ones
        let normalizedValue = control.value
          .replace(/’/g, "'")  // Smart apostrophe → straight apostrophe
          .replace(/“|”/g, '"') // Smart quotes → straight quotes
          .replace(/‐|–|—/g, '-') // Different dashes → hyphen
          .replace(/…/g, '...') // Ellipsis → three dots
          .replace(/\u00A0/g, ' '); // Non-breaking space → regular space

        return pattern.test(normalizedValue) ? null : { 'pattern': this.getErrorMessage() };
      };
    }
}

export class FormatUtility {

  private static translate: TranslateService;
  private static language: string;

  public static init(translate: TranslateService, language: string) {
    this.translate = translate;
    this.language = language;
  }

  public static setFmtItem(locale: Allows | Puncs, wordCase: WordCases = WordCases.LOWER, isNumber: boolean = false): AllowsDenys {
    return { locale, wordCase, isNumber };
  }

  public static setFmtItems(locales: (Allows | Puncs)[],
    overrides?: {
      wordCase?: Record<Allows | Puncs, WordCases>;
      isNumber?: Record<Allows | Puncs, boolean>;
  }): AllowsDenys[] {
    return locales.map(locale => {
      const wordCase = overrides?.wordCase?.[locale] ?? WordCases.LOWER;
      const isNumber = overrides?.isNumber?.[locale] ?? false;
      return this.setFmtItem(locale, wordCase, isNumber);
    });
  }

  // e.g. Allows numbers, letters and spaces.
  public static getAllows(als: AllowsDenys[]): string {
    let str = this.translate.instant('MESSAGE_ERRORS.ALLOWS') + ' ';
    return this.buildList(str, als);
  }

  // e.g. No space.
  public static getDenys(dys: AllowsDenys[]): string {
    let str = this.translate.instant('MESSAGE_ERRORS.NO') + ' ';
    return this.buildList(str, dys);
  }

  // e.g. Only numbers./Solo números.
  public static getOnly(only: AllowsDenys): string {
    let str = this.translate.instant('MESSAGE_ERRORS.ONLY_START') + ' ';
    return (str + this.checkWordCase(this.formatLast(only.locale), WordCases.LOWER));
  }

  // e.g. Numbers only./Solo números.
  public static getOnlyAtEnd(only: AllowsDenys): string {
    let str;
    if (this.language == 'en') {
      str = ' ' + this.translate.instant('MESSAGE_ERRORS.ONLY');
      return (this.checkWordCase(this.formatLast(only.locale), only.wordCase) + str);
    } else {
      return this.getOnly(only);
    }
  }

  // public static getMust() {}

  private static buildList(str: string, als: AllowsDenys[]): string {
    if (als.length == 1) {
      return (str + this.checkWordCase(this.formatLast(als[0].locale), als[0].wordCase));
    }
    if (als.length == 2) {
      return (str + ' ' + this.checkWordCase(this.formatSecondLast(als[0].locale), als[0].wordCase) + this.checkWordCase(this.formatLast(als[1].locale), als[1].wordCase));
    }
    als.forEach((a: AllowsDenys, idx: number, arr: AllowsDenys[])=>{
      if (idx < arr.length - 2) {
        str += this.checkWordCase(this.formatMiddle(a.locale), a.wordCase);
      } else {
        if (idx == arr.length - 2) str += this.checkWordCase(this.formatMiddle(a.locale), a.wordCase);
        else str += this.checkWordCase(this.formatLast(a.locale), a.wordCase);
      }
    })
    return str;
  }

  private static checkWordCase(strTranslated: string, wordCase: WordCases) {
    if (wordCase == WordCases.LOWER) return strTranslated;
    else if (wordCase == WordCases.UPPER) return strTranslated.toLocaleUpperCase(this.language);
    else return this.toInitialLocaleUpperCase(this.language, strTranslated);
  }

  private static formatMiddle(locale: Allows | Puncs): string {
    return (this.translate.instant(locale) + (locale.length > 1 ? Puncs.Comma : '') + ' ');
  }

  private static formatSecondLast(locale: Allows | Puncs): string {
    return (this.translate.instant(locale) + ' ' + this.translate.instant('MESSAGE_ERRORS.AND') + ' ');
  }

  private static formatLast(locale: Allows | Puncs): string {
    return (this.translate.instant(locale));
  }

  private static toInitialLocaleUpperCase(language: string, strTranslated: string): string {
    return strTranslated.charAt(0).toLocaleUpperCase(this.language) + strTranslated.slice(1)
  }
}

export interface AllowsDenys {
  locale: Allows | Puncs;
  wordCase: WordCases;
  isNumber: boolean;
}

export enum WordCases {
  LOWER = 'LOWER',
  UPPER = 'UPPER',
  INITIAL = 'INITIAL',
}

export enum Allows {
  Als_A_z = 'MESSAGE_ERRORS.UPPER_A_LOWER_Z',
  Als_0_9 = 'MESSAGE_ERRORS.NUMBER_0_9',
  Als_ALPHANUMERIC = 'MESSAGE_ERRORS.ALPHANUMERIC',
  Als_LETTERS = 'MESSAGE_ERRORS.LETTERS',
  Als_NUMBERS = 'MESSAGE_ERRORS.NUMBERS',
  Als_SPACES = 'MESSAGE_ERRORS.SPACES',

  Als_APOSTROPHE = 'MESSAGE_ERRORS.APOSTROPHE',
  Als_ASTERISK = 'MESSAGE_ERRORS.ASTERISK',
  Als_COLON = 'MESSAGE_ERRORS.COLON',
  Als_COMMA = 'MESSAGE_ERRORS.COMMA',
  Als_DASH = 'MESSAGE_ERRORS.DASH',
  Als_EXCLAM = 'MESSAGE_ERRORS.EXCLAMATION',
  Als_HYPHEN = 'MESSAGE_ERRORS.HYPHEN',
  Als_PARENTH_C = 'MESSAGE_ERRORS.PARENTH_C',
  Als_PARENTH_O = 'MESSAGE_ERRORS.PARENTH_O',
  Als_PERCENT = 'MESSAGE_ERRORS.PERCENT',
  Als_PERIOD = 'MESSAGE_ERRORS.PERIOD',
  Als_QUESTION = 'MESSAGE_ERRORS.QUESTION',
  Als_QUOTE = 'MESSAGE_ERRORS.QUOTE',
  Als_SPACE = 'MESSAGE_ERRORS.SPACE',
  Als_TIDE = 'MESSAGE_ERRORS.TIDE',
}

export enum Puncs {
  Ampersand = '&',
  Asterisk = '*',
  At = '@',
  Backslash = '\\',
  Backtick = '`',
  BraceClose = '}',
  BraceOpen = '{',
  BracketClose = ']',
  BracketOpen = '[',
  Caret = '^',
  Colon = ':',
  Comma = ',',
  Dash = '-',
  Dollar = '$',
  Exclamation = '!',
  GreaterThan = '>',
  Hash = '#',
  LessThan = '<',
  ParenthesisClose = ')',
  ParenthesisOpen = '(',
  Percent = '%',
  Period = '.',
  Pipe = '|',
  Question = '?',
  QuoteDouble = '"',
  QuoteSingle = "'",
  Semicolon = ';',
  Slash = '/',
  Tilde = '~',
  Underscore = '_',
}

/**
 * Below is the debug-version of the above code.
 * Comment out the above such that the below code can show the validation logic

import { AbstractControl, ValidationErrors, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { MeValidation } from "./me-validation";
import { ValidatorFn } from "@angular/forms";

export class MeFormatValidation extends MeValidation {

    errorKey: string;
    readablePattern: string;

    constructor(translate: TranslateService, pattern: string, errorKey: string = 'MESSAGE_ERROR_VALID_INPUT_ONLY', readablePattern: string = '') {
        super(translate, 'pattern');
        this.errorKey = errorKey;
        this.readablePattern = readablePattern;
        this.validators = MeFormatValidation.build(pattern, this.validationType);
    }

    getErrorMessage(): string {
        return this.translate.instant(this.errorKey) + ' ' + this.readablePattern;
    }

    static build(pattern : string, validationType : string): ValidatorFn {
        return MeFormatValidation.getValidatorFn(pattern, validationType);
    }


    static getValidatorFn = (pattern: string, validationType : string): ValidatorFn => {
        let regExp = new RegExp(pattern);
        return (control: AbstractControl): ValidationErrors | null => {

            console.log(control.value);
            if (control.value == null || control.value.length == 0) return null;
            if (regExp.test(control.value)) return null;
            
            let errorRecord: ValidationErrors = {'errorMsg': "Nice to meet you" };
            errorRecord[validationType] = true;
            return errorRecord;
        }
    }
}
*/